 import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PageHeaderScriptService {
  private readonly pageHeaderElementClass = 'page-header-script';
  private appliedPageHeaderScript = '';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.watchNavigationAwayFromPageHeaderScript();
    }
  }

  apply(pageHeaderScript = ''): void {
    const normalizedPageHeaderScript = pageHeaderScript?.trim();

    if (normalizedPageHeaderScript === this.appliedPageHeaderScript) {
      return;
    }

    this.removePageHeaderElements();
    this.appliedPageHeaderScript = normalizedPageHeaderScript || '';

    if (!normalizedPageHeaderScript) {
      return;
    }

    if (!this.hasHtmlTag(normalizedPageHeaderScript)) {
      this.appendExecutableScript(normalizedPageHeaderScript);
      return;
    }

    const wrapper = this.document.createElement('div');
    wrapper.innerHTML = normalizedPageHeaderScript;
    Array.from(wrapper.childNodes).forEach((node) => {
      this.appendPageHeaderNode(node);
    });
  }

  /**
   * Kod wklejany przez klienta w kreatorze to dowolny JavaScript — potrafi dopisać elementy do <body>,
   * ustawić globalne zmienne czy podpiąć nasłuchiwacze zdarzeń. Zdjęcie znaczników z <head> nie cofa
   * takich skutków ubocznych, więc przy zejściu z podstrony, na której kod się wykonał, wymuszamy pełne
   * przeładowanie dokumentu. Dopiero to gwarantuje, że kolejna podstrona nie odziedziczy elementów
   * poprzedniej. Podstrony bez kastomowego kodu zachowują zwykłą nawigację SPA.
   */
  private watchNavigationAwayFromPageHeaderScript(): void {
    this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe((event) => this.reloadWhenLeavingPageWithHeaderScript(event));
  }

  private reloadWhenLeavingPageWithHeaderScript(event: NavigationStart): void {
    if (!this.appliedPageHeaderScript || this.isSamePath(event.url)) {
      return;
    }

    if (event.navigationTrigger === 'popstate') {
      // Przy cofnięciu/przejściu w historii adres jest już docelowy, więc samo przeładowanie
      // wczytuje właściwą podstronę i nie dokłada nowego wpisu do historii.
      this.reloadDocument();
      return;
    }

    this.hardNavigate(event.url);
  }

  /**
   * Zmiana samych parametrów tej samej ścieżki (np. filtrów na liście prac) to wciąż ta sama podstrona —
   * jej kod ma dalej działać, więc nie przeładowujemy dokumentu.
   */
  private isSamePath(targetUrl: string): boolean {
    const targetPath = targetUrl.split('#')[0].split('?')[0];

    return this.normalizePath(targetPath) === this.normalizePath(this.document.location.pathname);
  }

  private normalizePath(path: string): string {
    return path.replace(/\/+$/, '') || '/';
  }

  private hardNavigate(url: string): void {
    this.document.location.href = url;
  }

  private reloadDocument(): void {
    this.document.location.reload();
  }

  private removePageHeaderElements(): void {
    const pageHeaderElements = this.document.querySelectorAll(`head .${this.pageHeaderElementClass}`);
    pageHeaderElements.forEach((element) => {
      element.remove();
    });
  }

  private hasHtmlTag(value: string): boolean {
    return /<[a-z][\s\S]*>/i.test(value);
  }

  private appendPageHeaderNode(node: ChildNode): void {
    // Gołego tekstu <head> i tak nie renderuje, a nie da się go oznaczyć markerem — zostałby w dokumencie
    // po zejściu z podstrony. Bierzemy więc pod uwagę wyłącznie elementy.
    if (node.nodeType !== 1) {
      return;
    }

    const existingElement = this.findExistingPageHeaderElement(node as Element);

    if (existingElement) {
      existingElement.classList.add(this.pageHeaderElementClass);
      return;
    }

    if (this.isScriptNode(node)) {
      this.appendExecutableScript(node.textContent || '', node as Element);
      return;
    }

    const element = node as Element;
    element.classList.add(this.pageHeaderElementClass);
    this.document.head.appendChild(element);
  }

  private isScriptNode(node: ChildNode): boolean {
    return node.nodeType === 1 && (node as Element).tagName.toLowerCase() === 'script';
  }

  private findExistingPageHeaderElement(sourceElement: Element): Element | null {
    return Array
      .from(this.document.head.children)
      .find((element) => this.isSamePageHeaderElement(sourceElement, element)) || null;
  }

  private isSamePageHeaderElement(sourceElement: Element, element: Element): boolean {
    if (sourceElement === element || sourceElement.tagName !== element.tagName) {
      return false;
    }

    const tagName = sourceElement.tagName.toLowerCase();

    if (tagName === 'script') {
      return this.isSameScriptElement(sourceElement, element);
    }

    if (tagName === 'meta') {
      return this.isSameMetaElement(sourceElement, element);
    }

    if (tagName === 'link') {
      const sourceRel = this.getNormalizedAttribute(sourceElement, 'rel');
      const sourceHref = this.getNormalizedAttribute(sourceElement, 'href');

      return !!(sourceRel || sourceHref)
        && sourceRel === this.getNormalizedAttribute(element, 'rel')
        && sourceHref === this.getNormalizedAttribute(element, 'href');
    }

    if (['style', 'noscript', 'title'].includes(tagName)) {
      return this.normalizeText(sourceElement.textContent) === this.normalizeText(element.textContent);
    }

    return this.normalizeElementHtml(sourceElement) === this.normalizeElementHtml(element);
  }

  private isSameScriptElement(sourceElement: Element, element: Element): boolean {
    const sourceSrc = this.getNormalizedAttribute(sourceElement, 'src');
    const elementSrc = this.getNormalizedAttribute(element, 'src');

    if (sourceSrc || elementSrc) {
      return sourceSrc === elementSrc;
    }

    return this.normalizeText(sourceElement.textContent) === this.normalizeText(element.textContent);
  }

  private isSameMetaElement(sourceElement: Element, element: Element): boolean {
    const identityAttributes = ['charset', 'name', 'property', 'http-equiv', 'itemprop'];

    return identityAttributes.some((attribute) => {
      const sourceValue = this.getNormalizedAttribute(sourceElement, attribute);
      return sourceValue && sourceValue === this.getNormalizedAttribute(element, attribute);
    }) && this.getNormalizedAttribute(sourceElement, 'content') === this.getNormalizedAttribute(element, 'content');
  }

  private normalizeElementHtml(element: Element): string {
    const clone = element.cloneNode(true) as Element;
    clone.classList.remove(this.pageHeaderElementClass);

    return clone.outerHTML.trim();
  }

  private normalizeText(value: string | null): string {
    return (value || '').trim();
  }

  private getNormalizedAttribute(element: Element, attribute: string): string {
    return (element.getAttribute(attribute) || '').trim();
  }

  private appendExecutableScript(content: string, sourceScript?: Element): void {
    const script = this.document.createElement('script');

    if (sourceScript) {
      Array.from(sourceScript.attributes).forEach((attribute) => {
        script.setAttribute(attribute.name, attribute.value);
      });
    }

    script.classList.add(this.pageHeaderElementClass);

    if (content) {
      script.textContent = content;
    }

    this.document.head.appendChild(script);
  }
}
